import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {
  ESTADO_TONE,
  esIncompleto,
  esListoParaValidar,
  estadoSinPaginas,
  type LotesResumen,
  type PaqueteSinPaginas,
} from '@/lib/bateria'

/** Sin páginas en una evaluación que sí recibió escaneos, «Por escanear» mentiría. Nunca dice «su
 * escaneo falló»: con el manifiesto vacío no se sabe de quién era cada archivo. */
function SinPaginasCelda({
  paqueteId,
  estado,
  onIrACuarentena,
}: {
  paqueteId: string
  estado: Exclude<PaqueteSinPaginas, 'por_escanear'>
  onIrACuarentena: () => void
}) {
  const {t} = useTranslation()
  const esperando = estado === 'esperando_identificacion'
  return (
    <div className="space-y-0.5">
      {/* U2: «Identificando» es trámite del sistema, no un escalón de magnitud. */}
      <Tag tone={esperando ? 'enCurso' : 'amber'} data-testid={`estado-${estado}-${paqueteId}`}>
        {t(`admin.bateria.detail.sinPaginas.${estado}`)}
      </Tag>
      {/* U5: los dos estados llevan salida; sin ella el chip queda sin acción ni porqué. */}
      <button
        type="button"
        data-testid={`sin-paginas-cuarentena-${paqueteId}`}
        onClick={onIrACuarentena}
        className="font-mono text-[11px] tracking-wider text-primary uppercase outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50">
        {t('admin.bateria.detail.irACuarentena')} →
      </button>
    </div>
  )
}

/** Transcribing with missing pages and no batch advancing is BLOCKED, not slow. */
function IncompletoCelda({paquete, onIrACuarentena}: {paquete: BateriaPaquete; onIrACuarentena: () => void}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-0.5">
      <Tag tone="amber" data-testid={`estado-incompleto-${paquete.id}`}>
        {t('admin.bateria.detail.estadoIncompleto')}
      </Tag>
      <p className="text-[11px] text-muted-foreground">
        {t('admin.bateria.detail.faltanPaginas', {count: paquete.paginas_faltantes.length})}
      </p>
      <button
        type="button"
        data-testid={`incompleto-cuarentena-${paquete.id}`}
        onClick={onIrACuarentena}
        className="font-mono text-[11px] tracking-wider text-primary uppercase outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/50">
        {t('admin.bateria.detail.irACuarentena')} →
      </button>
    </div>
  )
}

/** `transcrito` sin nada que decidir es el camino normal de un lote limpio, no «Por revisar». */
function claveDeEstado(paquete: BateriaPaquete): string {
  return esListoParaValidar(paquete) ? 'listoParaValidar' : paquete.estado
}

/** Resuelve los tres casos en que el enum crudo miente: bloqueado, sin páginas pese a los escaneos y
 * transcrito sin dudas. */
export function PaqueteEstadoCelda({
  paquete,
  lotes,
  batchRunning,
  onIrACuarentena,
}: {
  paquete: BateriaPaquete
  lotes: LotesResumen
  batchRunning: boolean
  onIrACuarentena: () => void
}) {
  const {t} = useTranslation()
  const bloqueado = esIncompleto(paquete, batchRunning)
  const sinPaginas = estadoSinPaginas(paquete, lotes)
  const clave = claveDeEstado(paquete)
  return (
    <div className="flex flex-wrap items-center gap-1">
      {bloqueado ? (
        <IncompletoCelda paquete={paquete} onIrACuarentena={onIrACuarentena} />
      ) : sinPaginas !== null && sinPaginas !== 'por_escanear' ? (
        <SinPaginasCelda paqueteId={paquete.id} estado={sinPaginas} onIrACuarentena={onIrACuarentena} />
      ) : (
        // U4: la escalera de estados se explica una vez, en el `HelpIcon` de la cabecera.
        <Tag
          tone={esListoParaValidar(paquete) ? 'green' : ESTADO_TONE[paquete.estado]}
          data-testid={`estado-${clave}-${paquete.id}`}>
          {t(`admin.bateria.paqueteEstado.${clave}`)}
        </Tag>
      )}
      {/* Visible «digitado manualmente» mark — part of the clinical record. */}
      {paquete.fuente !== 'omr' && (
        <Tag tone="amber" data-testid={`fuente-manual-${paquete.id}`}>
          {t('admin.bateria.detail.fuenteManual')}
        </Tag>
      )}
      {/* Pages rescued by OCR of the printed footer: weaker than a QR, so a human must check them. */}
      {paquete.paginas_por_pie.length > 0 && (
        <span className="flex items-center gap-1">
          <Tag tone="amber" data-testid={`origen-pie-${paquete.id}`}>
            {t('admin.bateria.detail.origenPie', {count: paquete.paginas_por_pie.length})}
          </Tag>
          <PorQue
            label={t('admin.bateria.detail.origenPiePorque')}
            ariaLabel={t('admin.bateria.detail.origenPiePorqueAria', {persona: paquete.employee_name})}
            testId={`origen-pie-porque-${paquete.id}`}
            contenidoTestId={`origen-pie-porque-contenido-${paquete.id}`}>
            {t('admin.bateria.detail.origenPieHelp', {paginas: paquete.paginas_por_pie.join(', ')})}
          </PorQue>
        </span>
      )}
    </div>
  )
}
